import { ShowcaseState } from '@/api/showcase'

/**
 * Шаг 4 активации — Обработка.
 *
 * Прогресс-бар из 4 этапов обработки + реалистичный срок «через 1-2 недели».
 * Polling делается родительским useShowcase (раз в 10 сек) — когда бэк переключит
 * status на active, ShowcaseRoute сам уйдёт с этого экрана на дашборд.
 */
export function StepProcessing({ state, onRefetch }: { state: ShowcaseState; onRefetch: () => void }) {
  return (
    <div className="step-processing">
      <div className="proc-icon">
        <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="32" cy="32" r="26" />
          <path d="M22 32l8 8 14-14" />
        </svg>
        <div className="proc-pulse" />
      </div>

      <h2 className="proc-title">Запустили — готовим страницы</h2>
      <p className="proc-sub">
        Создаём отдельную страницу для каждого поста на&nbsp;портале telegraphyx.ru. Дальше поисковики
        начнут их&nbsp;индексировать.
      </p>

      <div className="proc-stages">
        <div className="proc-stage done">
          <div className="proc-stage-dot">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
          <div className="proc-stage-body">
            <div className="proc-stage-title">Согласие принято</div>
            <div className="proc-stage-sub">Условия публикации зафиксированы</div>
          </div>
        </div>

        <div className="proc-stage active">
          <div className="proc-stage-dot">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="proc-spin">
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
            </svg>
          </div>
          <div className="proc-stage-body">
            <div className="proc-stage-title">Создаём страницы постов</div>
            <div className="proc-stage-sub">Готовим каждую к&nbsp;поиску (это пара минут)</div>
          </div>
        </div>

        <div className="proc-stage pending">
          <div className="proc-stage-dot" />
          <div className="proc-stage-body">
            <div className="proc-stage-title">Поисковики находят и&nbsp;индексируют</div>
            <div className="proc-stage-sub">Обычно 5-14&nbsp;дней</div>
          </div>
        </div>

        <div className="proc-stage pending">
          <div className="proc-stage-dot" />
          <div className="proc-stage-body">
            <div className="proc-stage-title">Первые читатели из&nbsp;поиска</div>
            <div className="proc-stage-sub">Когда страницы попадут в&nbsp;выдачу — пойдёт трафик</div>
          </div>
        </div>
      </div>

      <div className="proc-eta">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
        Первые читатели — <b>через 1-2&nbsp;недели</b>
      </div>

      <div className="proc-actions">
        <button className="cta-refresh" onClick={onRefetch}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 12a9 9 0 019-9c2.5 0 4.8 1 6.5 2.7L21 8M21 3v5h-5" />
            <path d="M21 12a9 9 0 01-9 9c-2.5 0-4.8-1-6.5-2.7L3 16M3 21v-5h5" />
          </svg>
          Проверить статус
        </button>
      </div>

      <p className="proc-foot">
        Можно закрыть эту страницу — дальше всё произойдёт само. Мы вернёмся сюда, когда страницы появятся.
      </p>

      <style>{`
        .step-processing {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 18px;
          text-align: center;
          max-width: 640px;
          margin: 0 auto;
          padding: 24px 0;
        }
        .proc-icon {
          position: relative;
          width: 80px;
          height: 80px;
          color: var(--tg-up, #00a860);
          margin-bottom: 4px;
        }
        .proc-icon svg { width: 100%; height: 100%; position: relative; z-index: 2; }
        .proc-pulse {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background: rgba(0, 168, 96, 0.15);
          animation: proc-pulse 1.8s ease-out infinite;
        }
        @keyframes proc-pulse {
          0% { transform: scale(0.6); opacity: 0.7; }
          100% { transform: scale(1.4); opacity: 0; }
        }
        .proc-title {
          font-size: 24px;
          font-weight: 800;
          margin: 0;
          color: var(--tg-ink, #111);
        }
        .proc-sub {
          font-size: 14.5px;
          color: var(--tg-mute, #5b6678);
          line-height: 1.55;
          margin: 0;
          max-width: 520px;
        }
        .proc-stages {
          display: flex;
          flex-direction: column;
          gap: 12px;
          align-self: stretch;
          margin: 12px 0;
          text-align: left;
        }
        .proc-stage {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 14px 16px;
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 12px;
        }
        .proc-stage-dot {
          width: 26px;
          height: 26px;
          border-radius: 50%;
          background: var(--tg-line-2, #f0f0f4);
          color: var(--tg-mute, #5b6678);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .proc-stage-dot svg { width: 14px; height: 14px; }
        .proc-stage.done .proc-stage-dot {
          background: var(--tg-up, #00a860);
          color: white;
        }
        .proc-stage.active .proc-stage-dot {
          background: var(--tg-blue, #0066ff);
          color: white;
        }
        .proc-stage.pending .proc-stage-dot {
          background: var(--tg-line-2, #f0f0f4);
        }
        .proc-stage.pending .proc-stage-body { opacity: 0.55; }
        .proc-stage-title {
          font-size: 14px;
          font-weight: 700;
          color: var(--tg-ink, #111);
          margin-bottom: 2px;
        }
        .proc-stage-sub {
          font-size: 12.5px;
          color: var(--tg-mute, #5b6678);
        }
        .proc-spin { animation: proc-spin 1.4s linear infinite; transform-origin: 12px 12px; }
        @keyframes proc-spin { to { transform: rotate(360deg); } }

        .proc-eta {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          background: var(--tg-paper, #fafafd);
          border-radius: 999px;
          font-size: 13.5px;
          color: var(--tg-ink-2, #333);
        }
        .proc-eta svg { width: 16px; height: 16px; color: var(--tg-mute, #5b6678); }
        .proc-eta b { color: var(--tg-ink, #111); font-weight: 800; }
        .proc-actions { margin-top: 4px; }
        .cta-refresh {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          background: var(--tg-surface, #fff);
          color: var(--tg-mute, #5b6678);
          font-weight: 600;
          font-size: 13px;
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 8px;
          cursor: pointer;
          font-family: inherit;
        }
        .cta-refresh:hover { background: var(--tg-paper, #fafafd); color: var(--tg-ink, #111); }
        .cta-refresh svg { width: 13px; height: 13px; }
        .proc-foot {
          font-size: 12px;
          color: var(--tg-mute, #5b6678);
          margin: 0;
          max-width: 480px;
        }
      `}</style>
    </div>
  )
}
